// Формата „Нова резервация" — чиста логика без React (тестове:
// newReservation.test.ts): кога по подразбиране, кои часове/минути са
// минали, предложение за маса, местата спрямо гостите и тялото на заявката.

import type { Reservation, ReservationCreateIn } from "../../../api/reservations";
import { MINUTE_MS, addDays, formatMinuteOfDay, minuteOfDay, momentAtMinute, toDateKey } from "./dayTime";
import { guestNameError, normalizeGuestName, phoneError } from "./guestFields";
import {
  SLOT_MINUTES,
  clampToShift,
  firstSlotFrom,
  hasOwnHours,
  isShiftOpen,
  isSlotInHours,
  lastSlotOf,
  ownMinuteOf,
  previousNightRangeOf,
} from "./workingSlots";

// Минутите на всеки час. Часовете идват от смяната на деня
// (`workingSlots.ts`; ненастроено → 11–22, последният слот 22:45).
export const FORM_MINUTES = [0, 15, 30, 45];
// Когато днес вече няма слот — следващият работен ден по това време.
const EVENING_SLOT = 19 * 60;
// Колко дни напред се търси работен ден (седмицата е цикъл).
const DAYS_IN_WEEK = 7;

// Продължителността: 1 ч / 1,5 ч / 2 ч (по подразбиране) / 3 ч.
export const DURATION_OPTIONS = [60, 90, 120, 180] as const;
export const DEFAULT_DURATION = 120;
export const DEFAULT_GUESTS = 2;
export const MAX_GUESTS = 500;
// Плочките с дни: днес + 6.
export const DAY_TILE_COUNT = 7;

/** Денят и часът на резервацията (минута от деня). */
export interface FormSlot {
  dateKey: string;
  minute: number;
}

/** Следващият кръгъл 15-минутен слот СЛЕД момента (19:10 → 19:15, 19:15 → 19:30). */
export const nextSlotMinute = (nowMs: number) =>
  Math.floor(minuteOfDay(nowMs) / SLOT_MINUTES) * SLOT_MINUTES + SLOT_MINUTES;

/**
 * Слотът е минал (само за днес — бъдещите дни винаги са свободни). При
 * РЕДАКЦИЯ (`originalSlot`) границата не е „сега", а началото на
 * резервацията, ако е по-рано: закъсняла 19:00 (сега 19:20) може да остане
 * 19:00 или да се премести на всеки час след него; преди него е минало.
 */
export const isSlotPast = (
  dateKey: string,
  minute: number,
  nowMs: number,
  originalSlot: FormSlot | null = null,
) => {
  const moment = momentAtMinute(dateKey, minute);
  if (moment > nowMs) return false;
  return !(originalSlot && moment >= momentAtMinute(originalSlot.dateKey, originalSlot.minute));
};

/** Целият час е минал (и последната му минута :45) — виж `isSlotPast`. */
export const isHourPast = (
  dateKey: string,
  hour: number,
  nowMs: number,
  originalSlot: FormSlot | null = null,
) => isSlotPast(dateKey, hour * 60 + FORM_MINUTES[FORM_MINUTES.length - 1], nowMs, originalSlot);

/** Най-ранният ден в избора: днес, а при редакция — и денят на резервацията, ако е по-ранен. */
export const firstDateKeyOf = (nowMs: number, originalSlot: FormSlot | null = null) => {
  const todayKey = toDateKey(new Date(nowMs));
  return originalSlot && originalSlot.dateKey < todayKey ? originalSlot.dateKey : todayKey;
};

/**
 * Следващият работен ден СЛЕД `dateKey` вечерта (19:00, приведено в
 * работното му време). Цяла седмица почивни дни → утре 19:00 (формата казва
 * „Почивен ден").
 */
const nextWorkingDaySlot = (dateKey: string): FormSlot => {
  for (let offset = 1; offset <= DAYS_IN_WEEK; offset += 1) {
    const candidate = addDays(dateKey, offset);
    if (hasOwnHours(candidate)) return { dateKey: candidate, minute: clampToShift(candidate, EVENING_SLOT) };
  }
  return { dateKey: addDays(dateKey, 1), minute: EVENING_SLOT };
};

/**
 * Привежда слота към позволен: закръглен нагоре до 15 мин и в работното
 * време на СВОЯТА смяна (преди отварянето → първият слот; след последния →
 * последния, т.е. НАЗАД — затова изборът от конкретен момент минава през
 * `slotAtMoment`, който не предлага час след последния слот), не в миналото
 * (тогава — следващият слот днес, после нощта на днешната смяна след
 * полунощ, а ако няма — следващият работен ден вечерта), не в минал ден.
 * Почивен ден остава избран (формата казва „Почивен ден" и спира записа).
 * При редакция (`originalSlot`) началото на резервацията и всичко след него
 * не е минало (`isSlotPast`) — оригиналният час остава.
 */
export const normalizeSlot = (
  slot: FormSlot,
  nowMs: number,
  originalSlot: FormSlot | null = null,
): FormSlot => {
  const todayKey = toDateKey(new Date(nowMs));
  const firstKey = firstDateKeyOf(nowMs, originalSlot);
  const dateKey = slot.dateKey < firstKey ? firstKey : slot.dateKey;
  if (originalSlot && isSameSlot({ dateKey, minute: slot.minute }, originalSlot)) return originalSlot;
  const rounded = Math.ceil(slot.minute / SLOT_MINUTES) * SLOT_MINUTES;
  if (!isShiftOpen(dateKey, rounded)) return { dateKey, minute: rounded };
  const minute = clampToShift(dateKey, rounded);
  if (!isSlotPast(dateKey, minute, nowMs, originalSlot)) return { dateKey, minute };
  // Минал ден се позволява само заради оригинала при редакция — друг час в него
  // връща оригинала, вместо да скача към утре.
  if (originalSlot && dateKey < todayKey) return originalSlot;
  if (dateKey === todayKey) {
    const next = firstSlotFrom(dateKey, nextSlotMinute(nowMs));
    if (next !== null) return { dateKey, minute: next };
    // Днешната смяна продължава след полунощ — първият ѝ час утре.
    const tomorrow = addDays(todayKey, 1);
    const night = previousNightRangeOf(tomorrow);
    if (night) return { dateKey: tomorrow, minute: night.firstSlot };
  }
  return nextWorkingDaySlot(todayKey);
};

/**
 * Слотът по подразбиране: от момента на времевата линия или следващият днес
 * (смяната в момента е почивен ден → следващият работен ден вечерта).
 */
export const initialSlot = (nowMs: number, startMs: number | null): FormSlot => {
  const moment = startMs ?? nowMs;
  const dateKey = toDateKey(new Date(moment));
  const minute = startMs === null ? nextSlotMinute(nowMs) : minuteOfDay(moment);
  if (startMs === null && !isShiftOpen(dateKey, minute)) return nextWorkingDaySlot(dateKey);
  return normalizeSlot({ dateKey, minute }, nowMs);
};

/**
 * Изборът на ДЕН (плочка или календар): часът остава, ако е в собствената
 * смяна на деня; иначе — отварянето ѝ (почивен ден → вечерта). Така
 * избраната плочка остава избрана — час след полунощ е от друга смяна.
 */
export const slotForDay = (
  dateKey: string,
  minute: number,
  nowMs: number,
  originalSlot: FormSlot | null = null,
): FormSlot => {
  // Редакция: денят на резервацията с нейния час остава както е (09:10 извън
  // работното време не става отварянето) — като в `normalizeSlot`.
  if (originalSlot && isSameSlot({ dateKey, minute }, originalSlot)) return originalSlot;
  return normalizeSlot({ dateKey, minute: ownMinuteOf(dateKey, minute, EVENING_SLOT) }, nowMs, originalSlot);
};

/**
 * Слотът за „Резервирай тази маса" от момента на времевата линия (денят
 * `dateKey`): първият слот в/след момента в СЪЩИЯ ден. null — няма такъв:
 * моментът е минал (преглед на по-ранен час или минал ден), след последния
 * слот на работното време, почивен ден или е полунощта в края на деня
 * (24:00). Момент преди отварянето → първият слот. Не връща час ПРЕДИ
 * избрания момент — бутонът тогава не се показва.
 */
export const slotAtMoment = (dateKey: string, momentMs: number, nowMs: number): FormSlot | null => {
  if (toDateKey(new Date(momentMs)) !== dateKey) return null;
  if (momentMs < Math.floor(nowMs / MINUTE_MS) * MINUTE_MS) return null;
  let minute = Math.ceil(minuteOfDay(momentMs) / SLOT_MINUTES) * SLOT_MINUTES;
  if (isSlotPast(dateKey, minute, nowMs)) minute = nextSlotMinute(nowMs);
  const slot = firstSlotFrom(dateKey, minute);
  return slot === null ? null : { dateKey, minute: slot };
};

/**
 * С какво се отваря „+ Нова резервация": днес — без нищо (следващият слот);
 * при разглеждан друг ден — неговият ден (момента от линията, но не след
 * последния слот на работното време, за да не прескочи в следващия ден).
 */
export const newReservationStartFor = (view: {
  isToday: boolean;
  dateKey: string;
  momentMs: number;
}): number | null => {
  if (view.isToday) return null;
  // Почивен ден: денят се отваря както е (формата казва „Почивен ден").
  const lastSlot = lastSlotOf(view.dateKey) ?? 24 * 60 - SLOT_MINUTES;
  const minute = toDateKey(new Date(view.momentMs)) === view.dateKey ? minuteOfDay(view.momentMs) : lastSlot;
  return momentAtMinute(view.dateKey, Math.min(minute, lastSlot));
};

/**
 * Смяна на часа: минутата остава, ако не е минала и е в работното време;
 * иначе — първата такава минута на часа (при редакция границата е началото
 * на резервацията — виж `isSlotPast`).
 */
export const slotWithHour = (
  slot: FormSlot,
  hour: number,
  nowMs: number,
  originalSlot: FormSlot | null = null,
): FormSlot => {
  const isFree = (candidate: number) =>
    isSlotInHours(slot.dateKey, candidate) && !isSlotPast(slot.dateKey, candidate, nowMs, originalSlot);
  const keep = hour * 60 + (slot.minute % 60);
  if (isFree(keep)) return { ...slot, minute: keep };
  const firstFree = FORM_MINUTES.map((minute) => hour * 60 + minute).find(isFree);
  return { ...slot, minute: firstFree ?? keep };
};

/** Интервалът на резервацията [from, to). */
export const slotInterval = (slot: FormSlot, durationMinutes: number) => {
  const fromMs = momentAtMinute(slot.dateKey, slot.minute);
  return { fromMs, toMs: fromMs + durationMinutes * MINUTE_MS };
};

/**
 * Резервациите, срещу които формата проверява заетостта: избраният ден +
 * съседните. Интервалът минава през полунощ (23:30 + 2 ч, 00:30 утре срещу
 * вчерашна от 23:30), а `GET /reservations?date=` връща само започналите в
 * деня — без съседите масата изглежда свободна и BE отговаря 409. Двойките
 * (настанена от по-ранен ден идва и в днешния отговор) се броят веднъж.
 */
export const reservationsAroundDay = (days: Reservation[][]): Reservation[] => {
  const byId = new Map<number, Reservation>();
  for (const day of days) for (const reservation of day) byId.set(reservation.id, reservation);
  return [...byId.values()];
};

/** Масата, както я вижда формата. */
export interface PickTable {
  id: number;
  name: string;
  seats: number;
  regionId: number;
}

/** Естествено сравнение на имената („2" < „10"). */
const byName = (left: PickTable, right: PickTable) =>
  left.name.localeCompare(right.name, undefined, { numeric: true });

/**
 * „☆ Предложи маса": най-малката незаета в този час маса, която побира
 * гостите; при равни места — първо в активния регион, после по име.
 */
export const suggestTable = (
  tables: PickTable[],
  guests: number,
  isBusy: (table: PickTable) => boolean,
  activeRegionId: number | null,
): PickTable | null =>
  tables
    .filter((table) => table.seats >= guests && !isBusy(table))
    .sort(
      (left, right) =>
        left.seats - right.seats ||
        Number(right.regionId === activeRegionId) - Number(left.regionId === activeRegionId) ||
        byName(left, right),
    )[0] ?? null;

/** Местата на избраните маси спрямо гостите („✓ 6 места за 4 гости"). */
export const seatsSummary = (tables: PickTable[], guests: number) => {
  const seats = tables.reduce((sum, table) => sum + table.seats, 0);
  return { seats, enough: seats >= guests };
};

/** Локално време без зона — BE го тълкува в часовата зона на заведението. */
export const toLocalStartAt = (slot: FormSlot) => `${slot.dateKey}T${formatMinuteOfDay(slot.minute)}:00`;

export interface NewReservationDraft {
  slot: FormSlot;
  durationMinutes: number;
  guests: number;
  tableIds: number[];
  phone: string;
  guestName: string;
  note: string;
}

/** Грешките на полетата на госта (null — полето е наред). */
export const guestErrorsOf = (draft: Pick<NewReservationDraft, "phone" | "guestName">) => ({
  phone: phoneError(draft.phone),
  guestName: guestNameError(draft.guestName),
});

/** „Създай резервация" е активен: има маса, валиден телефон и име. */
export const canSubmitDraft = (draft: NewReservationDraft) => {
  const errors = guestErrorsOf(draft);
  return draft.tableIds.length > 0 && errors.phone === null && errors.guestName === null;
};

/**
 * „Създай резервация" / „Запази промените" е активен: попълнена форма, поне
 * една съществуваща маса, известна заетост на деня, неминал час и НИТО ЕДНА
 * избрана маса, заета в интервала (застъпване или отворена сметка точно
 * преди началото) — BE би отказал същото с 409. Редакция със същите маси и
 * час (`keepsOriginal`, виж `keepsTablesAndTime`) не чака заетостта — нито
 * заетите маси, нито незаредения/неуспял ден я спират. Час извън работното
 * време (почивен ден) спира записа — виж `isSlotOutsideHours`.
 */
export const isFormSubmittable = (form: {
  draft: NewReservationDraft;
  selectedCount: number;
  busySelectedCount: number;
  isDayUnknown: boolean;
  slotPast: boolean;
  slotOutsideHours?: boolean;
  keepsOriginal?: boolean;
}) =>
  canSubmitDraft(form.draft) &&
  form.selectedCount > 0 &&
  (form.keepsOriginal === true || (form.busySelectedCount === 0 && !form.isDayUnknown)) &&
  !form.slotPast &&
  form.slotOutsideHours !== true;

/**
 * Тялото на POST /reservations (и PATCH). Името — нормализирано като в BE;
 * SMS потвърждението е скрито и не се праща (BE default false).
 */
export const buildCreatePayload = (draft: NewReservationDraft): ReservationCreateIn => {
  const note = draft.note.trim();
  return {
    startAt: toLocalStartAt(draft.slot),
    durationMinutes: draft.durationMinutes,
    guests: draft.guests,
    guestName: normalizeGuestName(draft.guestName),
    phone: draft.phone.trim(),
    ...(note ? { note } : {}),
    tableIds: draft.tableIds,
  };
};

/** Двата слота са един и същ ден и час. */
export const isSameSlot = (left: FormSlot, right: FormSlot) =>
  left.dateKey === right.dateKey && left.minute === right.minute;

/**
 * Слотът на съществуваща резервация — БЕЗ привеждане към 15 мин
 * (резервация през API в 09:10 остава 09:10, докато не я сменят).
 */
export const slotOfReservation = (reservation: Reservation): FormSlot => {
  const startMs = Date.parse(reservation.startAt);
  return { dateKey: toDateKey(new Date(startMs)), minute: minuteOfDay(startMs) };
};

/** Формата „Редакция на резервация", попълнена с данните на резервацията. */
export const draftFromReservation = (reservation: Reservation): NewReservationDraft => ({
  slot: slotOfReservation(reservation),
  durationMinutes: reservation.durationMinutes,
  guests: reservation.guests,
  tableIds: [...reservation.tableIds],
  phone: reservation.phone,
  guestName: reservation.guestName,
  note: reservation.note ?? "",
});

/**
 * Минал час спира записа — освен при редакция в/след началото на
 * резервацията: закъсняла резервация (19:00 в 19:20) трябва да може да смени
 * масата или бележката, без да ѝ местят часа (BE приема минал час при PATCH).
 */
export const isSlotBlockingSave = (
  slot: FormSlot,
  nowMs: number,
  originalSlot: FormSlot | null,
) => isSlotPast(slot.dateKey, slot.minute, nowMs, originalSlot);

/**
 * Часът е извън работното време на деня (или денят е почивен) — само формата
 * го спира, BE не проверява. При редакция оригиналният час на резервацията
 * НЕ се брои: по-стара резервация в друг час може да смени масата или
 * бележката, без да ѝ местят часа.
 */
export const isSlotOutsideHours = (slot: FormSlot, originalSlot: FormSlot | null) =>
  !(originalSlot && isSameSlot(slot, originalSlot)) && !isSlotInHours(slot.dateKey, slot.minute);

/**
 * Редакцията пази масите (като множество — редът не е смяна), началото и
 * продължителността на записаната резервация. Тогава „Запази" винаги минава
 * (като в BE `service_editing.update`): „масата в момента е заета" и
 * застъпването не се проверяват — гостите може вече да седят на масата, а
 * хостесата поправя само телефона или бележката.
 */
export const keepsTablesAndTime = (draft: NewReservationDraft, original: Reservation) => {
  if (!keepsTime(draft, original)) return false;
  const originalIds = new Set(original.tableIds);
  const draftIds = new Set(draft.tableIds);
  return draftIds.size === originalIds.size && [...draftIds].every((id) => originalIds.has(id));
};

/** Редакцията пази началото и продължителността на записаната резервация. */
export const keepsTime = (draft: NewReservationDraft, original: Reservation) =>
  isSameSlot(draft.slot, slotOfReservation(original)) && draft.durationMinutes === original.durationMinutes;

const NO_TABLE_IDS: ReadonlySet<number> = new Set();

/**
 * Масите на редактираната резервация, които пикерът не блокира като заети,
 * докато часът и продължителността не са сменени: махната по грешка (✕ на
 * чипа) маса се връща и изборът отново е „същите маси" (записът минава).
 */
export const reclaimableTableIds = (
  draft: NewReservationDraft,
  original: Reservation | null,
): ReadonlySet<number> => (original && keepsTime(draft, original) ? new Set(original.tableIds) : NO_TABLE_IDS);

/**
 * Избраните маси, които спират записа като заети в интервала — без масите,
 * които редактираната резервация вече държи при същия час (`reclaimable`):
 * гостите ѝ може вече да седят там, а добавянето на още една маса не я прави
 * „заета". Проверяват се само добавените (като BE `apply_update`).
 */
export const busySelectedTablesOf = <T extends { id: number }>(
  selectedTables: T[],
  busyTableIds: ReadonlySet<number>,
  reclaimable: ReadonlySet<number>,
): T[] => selectedTables.filter((table) => busyTableIds.has(table.id) && !reclaimable.has(table.id));
